/**
 * Composition Panel Component
 *
 * A specialized panel for displaying composition data (labels, languages, topics)
 * with overflow handling. Uses the generic OverflowPanel component internally.
 */

import type { CompositionEntry } from '@internal-audit/domain';

import { OverflowPanel, ProgressBar } from '../../../../core/components';

import * as styles from './CompositionPanel.css';

interface CompositionPanelProps {
  visibleItems: CompositionEntry[];
  hiddenItems: CompositionEntry[];
  totalItems: number;
  colorType: 'labels' | 'languages' | 'topics';
  title: string;
}

/**
 * Specialized overflow panel for composition data that shows progress bars
 * and provides accessible overflow handling with dialog expansion
 */
export function CompositionPanel({ visibleItems, hiddenItems, totalItems, colorType, title }: CompositionPanelProps) {
  // Color palettes for different types using XDS-compatible colors
  const getColor = (index: number, type: string) => {
    const colorPalettes = {
      labels: [
        '#22c55e', // Green
        '#3b82f6', // Blue
        '#f59e0b', // Amber
        '#ef4444', // Red
        '#8b5cf6', // Violet
        '#06b6d4', // Cyan
        '#f97316', // Orange
        '#ec4899', // Pink
      ],
      languages: [
        '#22c55e', // Green
        '#3b82f6', // Blue
        '#f59e0b', // Amber
        '#ef4444', // Red
        '#8b5cf6', // Violet
        '#06b6d4', // Cyan
        '#f97316', // Orange
        '#ec4899', // Pink
      ],
      topics: [
        '#06b6d4', // Cyan
        '#f97316', // Orange
        '#ec4899', // Pink
        '#84cc16', // Lime
        '#a855f7', // Purple
        '#22c55e', // Green
        '#3b82f6', // Blue
        '#f59e0b', // Amber
      ],
    };

    const palette = colorPalettes[type as keyof typeof colorPalettes] || colorPalettes.labels;

    return palette[index % palette.length];
  };

  // Map color to ProgressBar color variants
  const getProgressBarColor = (color: string): 'primary' | 'success' | 'warning' | 'error' | 'secondary' => {
    if (color === '#22c55e') return 'success'; // Green
    if (color === '#ef4444') return 'error'; // Red
    if (color === '#f59e0b' || color === '#f97316') return 'warning'; // Amber/Orange
    if (color === '#8b5cf6' || color === '#a855f7') return 'secondary'; // Violet/Purple

    return 'primary'; // Blue and others
  };

  const renderCompositionItems = (items: CompositionEntry[]) =>
    items.map((entry, index) => {
      const percentage = entry.percentage || (totalItems > 0 ? Math.round((entry.count / totalItems) * 100) : 0);
      const color = getColor(index, colorType);
      const progressBarColor = getProgressBarColor(color);

      return (
        <div key={entry.key} className={styles.compositionBarItem}>
          <div className={styles.compositionBarLabel}>
            <div className={styles.compositionBarColor} style={{ backgroundColor: color }} />
            <span className={styles.compositionBarText}>{entry.key}</span>
          </div>
          <ProgressBar
            animated={false}
            aria-label={`${entry.key}: ${entry.count} items (${percentage}%)`}
            color={progressBarColor}
            size="panel"
            value={percentage}
          />
          <span className={styles.compositionBarValue}>
            {entry.count} ({percentage}%)
          </span>
        </div>
      );
    });

  const hasOverflow = hiddenItems.length > 0;
  const allItems = [...visibleItems, ...hiddenItems];

  // Dialog content showing all items in a grid layout
  const dialogContent = (
    <div className={styles.dialogItemsList}>
      {allItems.map((entry, index) => {
        const percentage = entry.percentage || (totalItems > 0 ? Math.round((entry.count / totalItems) * 100) : 0);
        const color = getColor(index, colorType);

        return (
          <div key={entry.key} className={styles.dialogCompositionItem}>
            <div className={styles.dialogCompositionLabel}>
              <div className={styles.dialogCompositionColorDot} style={{ backgroundColor: color }} />
              <span className={styles.dialogCompositionText} title={entry.key}>
                {entry.key}
              </span>
            </div>
            <span className={styles.dialogCompositionValue}>
              {entry.count} ({percentage}%)
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <OverflowPanel
      className={styles.panelContent}
      dialogContent={dialogContent}
      dialogDescription={`Showing all ${allItems.length} items from the ${title.toLowerCase()} breakdown:`}
      dialogTitle={`Complete ${title}`}
      hasOverflow={hasOverflow}
      overflowAriaLabel={`View all ${allItems.length} ${title.toLowerCase()}`}
      overflowText={`+${hiddenItems.length} more`}
    >
      {renderCompositionItems(visibleItems)}
    </OverflowPanel>
  );
}
